import { useState, useMemo, useRef, useEffect } from 'react'
import { getApiBase } from '../utils/api'
import type { Product } from '../types'
import {
  IoTDeviceStatuses,
  IoTDeviceTypes,
  type IoTDevice,
} from '@esf/shared'
import Modal from './Modal'

// Status badge component
const IoTStatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, string> = {
    ONLINE: 'bg-success-100 text-success-700',
    OFFLINE: 'bg-gray-100 text-gray-700',
    ERROR: 'bg-error-100 text-error-700',
    MAINTENANCE: 'bg-warning-100 text-warning-700',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  )
}

// Simple icon renderer
const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

interface IoTDashboardPageProps {
  iotDevices: IoTDevice[]
  products: Product[]
  iotStatusFilter: string
  setIotStatusFilter: (v: string) => void
  iotTypeFilter: string
  setIotTypeFilter: (v: string) => void
  iotSearchQuery: string
  setIotSearchQuery: (v: string) => void
  iotSortBy: 'id' | 'deviceType' | 'status' | 'batteryLevel' | 'lastSeenAt'
  setIotSortBy: (v: 'id' | 'deviceType' | 'status' | 'batteryLevel' | 'lastSeenAt') => void
  iotSortDir: 'asc' | 'desc'
  setIotSortDir: (v: 'asc' | 'desc') => void
  iotExporting: boolean
  setIotExporting: (v: boolean) => void
  showIotSavedReports: boolean
  setShowIotSavedReports: (v: boolean) => void
  iotSavedReports: Array<{ key: string; size: number; lastModified: string; filename?: string }>
  setIotSavedReports: (v: Array<{ key: string; size: number; lastModified: string; filename?: string }>) => void
  iotSavedReportsLoading: boolean
  setIotSavedReportsLoading: (v: boolean) => void
  setSelectedIotDevice: (device: IoTDevice) => void
  setIotDeviceDetailModalOpen: (v: boolean) => void
  setSelectedProduct: (product: Product) => void
  setProductDetailModalOpen: (v: boolean) => void
  fetchSensorReadings: (deviceId: string) => void
  fetchIotDevices: () => void
  setAlertModal: (v: { isOpen: boolean; title: string; message: string; type: 'info' | 'success' | 'error' | 'warning' }) => void
}

export default function IoTDashboardPage({
  iotDevices,
  products,
  iotStatusFilter,
  setIotStatusFilter,
  iotTypeFilter,
  setIotTypeFilter,
  iotSearchQuery,
  setIotSearchQuery,
  iotSortBy,
  setIotSortBy,
  iotSortDir,
  setIotSortDir,
  iotExporting,
  setIotExporting,
  showIotSavedReports,
  setShowIotSavedReports,
  iotSavedReports,
  setIotSavedReports,
  iotSavedReportsLoading,
  setIotSavedReportsLoading,
  setSelectedIotDevice,
  setIotDeviceDetailModalOpen,
  setSelectedProduct,
  setProductDetailModalOpen,
  fetchSensorReadings,
  fetchIotDevices,
  setAlertModal,
}: IoTDashboardPageProps) {
  const deviceTypeLabels: Record<string, string> = {
    OCCUPANCY_SENSOR: 'Occupancy Sensor',
    SOLAR_MONITOR: 'Solar Monitor',
    WASTE_LEVEL: 'Waste Level',
    AIR_QUALITY: 'Air Quality',
    TEMPERATURE: 'Temperature',
    HUMIDITY: 'Humidity',
    NOISE_LEVEL: 'Noise Level',
    LIGHT_SENSOR: 'Light Sensor',
    STRUCTURAL_MONITOR: 'Structural Monitor',
  }

  // Local search input state for immediate feedback
  const [searchInput, setSearchInput] = useState(iotSearchQuery)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Sync searchInput with iotSearchQuery when it changes externally (e.g., clear filters)
  useEffect(() => {
    setSearchInput(iotSearchQuery)
  }, [iotSearchQuery])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setIotSearchQuery(value)
    }, 300)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const getProductName = (assetId: string) => {
    const product = products.find(p => p.id === assetId)
    return product?.name || assetId
  }

  const getProduct = (assetId: string) => {
    return products.find(p => p.id === assetId) || null
  }

  // Filter and sort devices
  const filteredDevices = useMemo(() => {
    let filtered = iotDevices.filter(device => {
      if (iotStatusFilter && device.status !== iotStatusFilter) return false
      if (iotTypeFilter && device.deviceType !== iotTypeFilter) return false
      if (iotSearchQuery) {
        const q = iotSearchQuery.toLowerCase()
        const productName = getProductName(device.assetId).toLowerCase()
        return device.id.toLowerCase().includes(q) ||
               device.deviceType.toLowerCase().includes(q) ||
               productName.includes(q) ||
               device.manufacturer?.toLowerCase().includes(q) ||
               device.model?.toLowerCase().includes(q)
      }
      return true
    })

    // Sort
    filtered.sort((a, b) => {
      let aVal: string | number, bVal: string | number
      switch (iotSortBy) {
        case 'id':
          aVal = a.id.toLowerCase()
          bVal = b.id.toLowerCase()
          break
        case 'deviceType':
          aVal = a.deviceType.toLowerCase()
          bVal = b.deviceType.toLowerCase()
          break
        case 'status':
          aVal = a.status
          bVal = b.status
          break
        case 'batteryLevel':
          aVal = a.batteryLevel ?? -1
          bVal = b.batteryLevel ?? -1
          break
        case 'lastSeenAt':
        default:
          aVal = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0
          bVal = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return iotSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return iotSortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number)
    })

    return filtered
  }, [iotDevices, iotStatusFilter, iotTypeFilter, iotSearchQuery, iotSortBy, iotSortDir, products])

  // Calculate stats
  const onlineCount = iotDevices.filter(d => d.status === 'ONLINE').length
  const offlineCount = iotDevices.filter(d => d.status === 'OFFLINE').length
  const errorCount = iotDevices.filter(d => d.status === 'ERROR').length
  const lowBatteryCount = iotDevices.filter(d => d.batteryLevel !== undefined && d.batteryLevel < 20).length

  // Open device detail
  const handleDeviceClick = (device: IoTDevice) => {
    setSelectedIotDevice(device)
    setIotDeviceDetailModalOpen(true)
    fetchSensorReadings(device.id)
  }

  // Open product detail from IoT page
  const handleAssetClick = (e: React.MouseEvent, assetId: string) => {
    e.stopPropagation()
    const product = getProduct(assetId)
    if (product) {
      setSelectedProduct(product)
      setProductDetailModalOpen(true)
    }
  }

  // Format relative time
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  // Sort column click handler
  const handleSort = (column: typeof iotSortBy) => {
    if (iotSortBy === column) {
      setIotSortDir(iotSortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setIotSortBy(column)
      setIotSortDir('desc')
    }
  }

  // Sort indicator
  const renderSortIndicator = (column: typeof iotSortBy) => {
    if (iotSortBy !== column) return <span className="text-gray-300 ml-1">↕</span>
    return <span className="text-primary-600 ml-1">{iotSortDir === 'asc' ? '↑' : '↓'}</span>
  }

  // CSV Export
  const exportToCSV = () => {
    const headers = ['Device ID', 'Type', 'Asset', 'Asset ID', 'Status', 'Battery Level (%)', 'Signal Strength (dBm)', 'Last Seen', 'Manufacturer', 'Model', 'Firmware']
    const rows = filteredDevices.map(d => [
      d.id,
      deviceTypeLabels[d.deviceType] || d.deviceType,
      getProductName(d.assetId),
      d.assetId,
      d.status,
      d.batteryLevel ?? '',
      d.signalStrength ?? '',
      d.lastSeenAt || '',
      d.manufacturer || '',
      d.model || '',
      d.firmwareVersion || ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `iot-devices-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  // S3 Export
  const exportToS3 = async () => {
    setIotExporting(true)
    try {
      const headers = ['Device ID', 'Type', 'Asset', 'Asset ID', 'Status', 'Battery Level (%)', 'Signal Strength (dBm)', 'Last Seen', 'Manufacturer', 'Model', 'Firmware']
      const rows = filteredDevices.map(d => [
        d.id,
        deviceTypeLabels[d.deviceType] || d.deviceType,
        getProductName(d.assetId),
        d.assetId,
        d.status,
        d.batteryLevel ?? '',
        d.signalStrength ?? '',
        d.lastSeenAt || '',
        d.manufacturer || '',
        d.model || '',
        d.firmwareVersion || ''
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      const response = await fetch(`${getApiBase()}/iot/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: csvContent,
          filename: `iot-devices-${new Date().toISOString().split('T')[0]}.csv`
        })
      })

      if (!response.ok) throw new Error('Export failed')

      setAlertModal({
        isOpen: true,
        title: 'Export Successful',
        message: `Report saved successfully! Click "View Saved Reports" to access it.`,
        type: 'success'
      })
      fetchIotSavedReports()
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: 'Export Failed',
        message: 'Failed to save report to online storage. Please try again.',
        type: 'error'
      })
    } finally {
      setIotExporting(false)
    }
  }

  // Fetch saved IoT reports from S3
  const fetchIotSavedReports = async () => {
    setIotSavedReportsLoading(true)
    try {
      const response = await fetch(`${getApiBase()}/iot/reports`)
      if (!response.ok) throw new Error('Failed to fetch')
      const data = await response.json()
      setIotSavedReports(data.reports || [])
    } catch (err) {
      console.error('Failed to fetch saved IoT reports:', err)
      setIotSavedReports([])
    } finally {
      setIotSavedReportsLoading(false)
    }
  }

  // Download a saved report
  const downloadReport = (key: string) => {
    window.location.href = `${getApiBase()}/iot/reports/download?key=${encodeURIComponent(key)}`
  }

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Load saved reports when modal opens
  const handleOpenSavedReports = () => {
    setShowIotSavedReports(true)
    fetchIotSavedReports()
  }

  // Clear all filters
  const clearFilters = () => {
    setIotStatusFilter('')
    setIotTypeFilter('')
    setSearchInput('')
    setIotSearchQuery('')
  }

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">IoT Dashboard</h1>
        <div className="flex gap-2">
          <button onClick={fetchIotDevices} className="btn btn-secondary">
            {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
            Refresh
          </button>
          <button onClick={exportToCSV} className="btn btn-secondary" disabled={filteredDevices.length === 0}>
            {renderIcon('M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z')}
            Export CSV
          </button>
          <button
            onClick={exportToS3}
            className="btn btn-primary"
            disabled={filteredDevices.length === 0 || iotExporting}
          >
            {iotExporting ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Saving...
              </>
            ) : (
              <>
                {renderIcon('M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12')}
                Save to Online Storage
              </>
            )}
          </button>
          <button
            onClick={handleOpenSavedReports}
            className="btn btn-outline"
          >
            {renderIcon('M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4')}
            View Saved Reports
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="kpi cursor-pointer hover:shadow-md transition-shadow" onClick={() => setIotStatusFilter('')}>
          <div className="kpi-icon bg-info-100 text-info-600">
            {renderIcon('M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z')}
          </div>
          <div className="kpi-title">Total</div>
          <div className="kpi-value">{iotDevices.length}</div>
        </div>
        <div className={`kpi cursor-pointer hover:shadow-md transition-shadow ${iotStatusFilter === 'ONLINE' ? 'ring-2 ring-success-500' : ''}`} onClick={() => setIotStatusFilter(iotStatusFilter === 'ONLINE' ? '' : 'ONLINE')}>
          <div className="kpi-icon bg-success-100 text-success-600">
            {renderIcon('M5 13l4 4L19 7')}
          </div>
          <div className="kpi-title">Online</div>
          <div className="kpi-value text-success-600">{onlineCount}</div>
        </div>
        <div className={`kpi cursor-pointer hover:shadow-md transition-shadow ${iotStatusFilter === 'OFFLINE' ? 'ring-2 ring-gray-500' : ''}`} onClick={() => setIotStatusFilter(iotStatusFilter === 'OFFLINE' ? '' : 'OFFLINE')}>
          <div className="kpi-icon bg-gray-100 text-gray-600">
            {renderIcon('M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636')}
          </div>
          <div className="kpi-title">Offline</div>
          <div className="kpi-value text-gray-600">{offlineCount}</div>
        </div>
        <div className={`kpi cursor-pointer hover:shadow-md transition-shadow ${iotStatusFilter === 'ERROR' ? 'ring-2 ring-error-500' : ''}`} onClick={() => setIotStatusFilter(iotStatusFilter === 'ERROR' ? '' : 'ERROR')}>
          <div className="kpi-icon bg-error-100 text-error-600">
            {renderIcon('M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z')}
          </div>
          <div className="kpi-title">Errors</div>
          <div className="kpi-value text-error-600">{errorCount}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-warning-100 text-warning-600">
            {renderIcon('M13 10V3L4 14h7v7l9-11h-7z')}
          </div>
          <div className="kpi-title">Low Battery</div>
          <div className="kpi-value text-warning-600">{lowBatteryCount}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                className="filter-select w-full"
                placeholder="Search devices, assets, manufacturers..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="filter-select"
                value={iotStatusFilter}
                onChange={(e) => setIotStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                {IoTDeviceStatuses.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                className="filter-select"
                value={iotTypeFilter}
                onChange={(e) => setIotTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                {IoTDeviceTypes.map(t => (
                  <option key={t} value={t}>{deviceTypeLabels[t] || t}</option>
                ))}
              </select>
            </div>
            {(iotStatusFilter || iotTypeFilter || searchInput) && (
              <button
                onClick={clearFilters}
                className="btn btn-outline text-sm"
              >
                Clear Filters
              </button>
            )}
          </div>
          {(iotStatusFilter || iotTypeFilter || iotSearchQuery) && (
            <div className="mt-3 text-sm text-gray-500">
              Showing {filteredDevices.length} of {iotDevices.length} devices
            </div>
          )}
        </div>
      </div>

      {/* Devices Table */}
      <div className="table-card">
        <table className="table">
          <thead>
            <tr>
              <th
                className="cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('id')}
              >
                Device ID {renderSortIndicator('id')}
              </th>
              <th
                className="cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('deviceType')}
              >
                Type {renderSortIndicator('deviceType')}
              </th>
              <th>Asset</th>
              <th
                className="cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('status')}
              >
                Status {renderSortIndicator('status')}
              </th>
              <th
                className="cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('batteryLevel')}
              >
                Battery {renderSortIndicator('batteryLevel')}
              </th>
              <th>Signal</th>
              <th
                className="cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('lastSeenAt')}
              >
                Last Seen {renderSortIndicator('lastSeenAt')}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredDevices.map(device => (
              <tr
                key={device.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleDeviceClick(device)}
              >
                <td className="font-mono text-sm">{device.id}</td>
                <td className="text-sm">{deviceTypeLabels[device.deviceType] || device.deviceType}</td>
                <td>
                  <button
                    className="text-sm text-esf-navy hover:underline font-medium"
                    onClick={(e) => handleAssetClick(e, device.assetId)}
                  >
                    {getProductName(device.assetId)}
                  </button>
                </td>
                <td>
                  <IoTStatusBadge status={device.status} />
                </td>
                <td>
                  {device.batteryLevel !== undefined ? (
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            device.batteryLevel > 50 ? 'bg-success-500' :
                            device.batteryLevel > 20 ? 'bg-warning-500' : 'bg-error-500'
                          }`}
                          style={{ width: `${device.batteryLevel}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{device.batteryLevel}%</span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">Wired</span>
                  )}
                </td>
                <td className="text-sm text-gray-500">
                  <span className={`${device.signalStrength && device.signalStrength > -50 ? 'text-success-600' : device.signalStrength && device.signalStrength > -70 ? 'text-warning-600' : 'text-gray-500'}`}>
                    {device.signalStrength ? `${device.signalStrength} dBm` : '-'}
                  </span>
                </td>
                <td className="text-sm text-gray-500">
                  {device.lastSeenAt ? (
                    <span title={new Date(device.lastSeenAt).toLocaleString()}>
                      {formatRelativeTime(device.lastSeenAt)}
                    </span>
                  ) : '-'}
                </td>
                <td>
                  <button
                    className="text-gray-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeviceClick(device)
                    }}
                  >
                    {renderIcon('M9 5l7 7-7 7')}
                  </button>
                </td>
              </tr>
            ))}
            {filteredDevices.length === 0 && iotDevices.length > 0 && (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state">
                    <div className="empty-state-icon bg-gray-100 text-gray-600">
                      {renderIcon('M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z')}
                    </div>
                    <div className="empty-state-title">No Matching Devices</div>
                    <div className="empty-state-text">
                      Try adjusting your search or filters.
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {iotDevices.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state">
                    <div className="empty-state-icon bg-info-100 text-info-600">
                      {renderIcon('M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z')}
                    </div>
                    <div className="empty-state-title">No IoT Devices</div>
                    <div className="empty-state-text">
                      IoT devices attached to your assets will appear here.
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Saved Reports Modal */}
      <Modal
        isOpen={showIotSavedReports}
        onClose={() => setShowIotSavedReports(false)}
        title="Saved IoT Reports"
        size="lg"
      >
        <div className="space-y-4">
          {iotSavedReportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="animate-spin inline-block w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full mr-3" />
              Loading saved reports...
            </div>
          ) : iotSavedReports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="mb-2">
                {renderIcon('M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4')}
              </div>
              <p>No saved reports yet.</p>
              <p className="text-sm mt-1">Use "Save to Online Storage" to save your first report.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {iotSavedReports.map((report, index) => (
                <div key={report.key || index} className="py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {report.filename || report.key?.split('/').pop()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(report.lastModified).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {report.size && ` • ${formatFileSize(report.size)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => downloadReport(report.key)}
                    className="btn btn-sm btn-secondary ml-4"
                  >
                    {renderIcon('M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z')}
                    Download
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={() => setShowIotSavedReports(false)}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
